import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CertificateController from '../controllers/CertificateController';

const VerifyView = () => {
  const { id, hash } = useParams();
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('id');

  useEffect(() => {
    if (id) {
      setInputValue(id);
      setVerificationMethod('id');
      handleVerification(id, 'id');
    } else if (hash) {
      setInputValue(hash);
      setVerificationMethod('hash');
      handleVerification(hash, 'hash');
    }
  }, [id, hash]);

  const handleVerification = async (value, method) => {
    if (!value.trim()) return;

    setLoading(true);
    setVerificationData(null);

    const result = await CertificateController.verifyCertificate(value, method);
    setVerificationData(result);
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerification(inputValue, verificationMethod);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="verify-certificate-page">
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="text-center mb-12">
          <h1 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem', color: 'var(--primary-color)' }}>
            Institutional Verification
          </h1>
          <p style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>
            Cryptographically verify academic credentials on the blockchain.
          </p>
        </div>

        <div className="card mb-12">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="flex justify-center gap-8 mb-8">
                {['id', 'hash', 'qr'].map(m => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="method"
                      value={m}
                      checked={verificationMethod === m}
                      onChange={(e) => setVerificationMethod(e.target.value)}
                    />
                    <span style={{ textTransform: 'uppercase', fontWeight: '600' }}>
                      {m === 'id' ? 'ID' : m === 'hash' ? 'Hash' : 'QR'}
                    </span>
                  </label>
                ))}
              </div>

              <div className="form-group mb-6">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="form-input text-center py-4"
                  style={{ fontSize: '1.1rem' }}
                  placeholder={
                    verificationMethod === 'id' ? 'Enter Certificate ID' :
                      verificationMethod === 'hash' ? 'Enter Blockchain Hash' :
                        'Enter QR Data'
                  }
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary w-full py-4 text-lg"
                disabled={loading || !inputValue.trim()}
              >
                {loading ? 'Verifying...' : 'Verify Record'}
              </button>
            </form>
          </div>
        </div>

        {verificationData && (
          <div className={`verification-result mb-12 ${verificationData.isValid ? 'verification-valid' : 'verification-invalid'}`}>
            <div className="verification-icon">
              {verificationData.isValid ? '✓' : '✗'}
            </div>
            <div className="verification-message">
              {verificationData.isValid ? 'CERTIFICATE VALID' : 'VERIFICATION FAILED'}
            </div>
            <div className="verification-details">
              {verificationData.message || (verificationData.isValid ? 'The blockchain record is authentic and matches the provided details.' : 'The record could not be found or has been tampered with.')}
            </div>
          </div>
        )}

        {verificationData?.isValid && verificationData.data?.certificate && (
          <div className="card">
            <div className="card-header"><h2 className="card-title">Verified Academic Record</h2></div>
            <div className="card-body">
              <div className="certificate">
                <div className="certificate-header">
                  <h3 className="certificate-title">{verificationData.data.certificate.academicData?.degree}</h3>
                  <p className="certificate-subtitle">{verificationData.data.college?.name}</p>
                </div>

                <div className="certificate-body">
                  <div className="certificate-student">
                    {verificationData.data.certificate.studentData?.name}
                  </div>
                  <div className="grid grid-cols-2 gap-8 text-left mt-8">
                    <div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Student ID</div>
                      <div style={{ fontWeight: '600' }}>{verificationData.data.certificate.studentData?.studentId}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Graduation Year</div>
                      <div style={{ fontWeight: '600' }}>{verificationData.data.certificate.academicData?.graduationYear}</div>
                    </div>
                  </div>
                </div>

                {verificationData.data.certificate.blockchain && (
                  <div className="mt-8 p-6" style={{ backgroundColor: 'var(--background-color)', borderRadius: 'var(--border-radius)', textAlign: 'left' }}>
                    <h4 className="mb-4">Blockchain Details</h4>
                    <div className="flex flex-col gap-2" style={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      <p><strong>TOKEN:</strong> #{verificationData.data.certificate.tokenId}</p>
                      <p style={{ wordBreak: 'break-all' }}><strong>HASH :</strong> {verificationData.data.certificate.blockchain.certificateHash}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyView;